import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Tags } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { aplicarCambiosEstructura, tarjetaEstructura, type ResultadoAplicar } from "@/lib/estructura/aplicarCambios";

type Estado = "idle" | "saving" | "saved" | "discarded" | "error";

const MUESTRA = 8;

/**
 * «Guardar» los datos de la estructura que preparó el asistente (23-10-2026,
 * tanda 3b de «Campos y numeración»): campos nuevos, valores dictados o sacados
 * de un Excel, y numeraciones. Aquí se ve QUÉ se guarda —dónde casó cada fila,
 * marcadas las aproximadas— y lo que NO casó, que no se guarda. Nada cambia hasta
 * pulsar el botón; guarda el navegador con la sesión del admin.
 */
export function AssistantEstructuraCambioCard({ item }: { item: Record<string, unknown> }) {
  const { t } = useTranslation();
  const { company, user, role } = useAuth();
  const qc = useQueryClient();
  const [estado, setEstado] = useState<Estado>("idle");
  const [resultado, setResultado] = useState<ResultadoAplicar | null>(null);
  const [verTodo, setVerTodo] = useState(false);
  const tarjeta = useMemo(() => tarjetaEstructura(item), [item]);

  if (!tarjeta) {
    return (
      <div data-testid="assistant-estructura-cambio-invalid" className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        {t("settings.structureFields.card.invalid")}
      </div>
    );
  }

  const esAdmin = role === "admin";
  const crear = tarjeta.ops.filter((o) => o.op === "crear_campo");
  const valores = tarjeta.ops.filter((o) => o.op === "poner_valor");
  const numerar = tarjeta.ops.filter((o) => o.op === "numerar");
  const visibles = verTodo ? valores : valores.slice(0, MUESTRA);

  const guardar = async () => {
    if (!company?.id || estado === "saving" || !esAdmin) return;
    setEstado("saving");
    const r = await aplicarCambiosEstructura(supabase, company.id, user?.id ?? null, item);
    setResultado(r);
    if (!r.ok) {
      setEstado("error");
      return;
    }
    setEstado("saved");
    for (const k of ["estructura-campos", "estructura-valores", "estructura-datos"]) void qc.invalidateQueries({ queryKey: [k] });
  };

  const motivo = resultado && "motivo" in resultado ? resultado.motivo : null;
  const error = motivo === "permiso" ? t("settings.structureFields.card.onlyAdmin") : motivo === "invalida" ? t("settings.structureFields.card.invalid") : motivo ? t("settings.structure.saveFailed") : "";

  return (
    <div data-testid="assistant-estructura-cambio-card" className="mt-2 w-full rounded-2xl border border-[#b6dded] bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#1F9E57] text-white">
          <Tags className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("settings.structureFields.card.title")}</span>
          <span className="block text-sm font-bold text-foreground">
            {[
              crear.length ? t("settings.structureFields.card.newFields", { count: crear.length }) : null,
              valores.length ? t("settings.structureFields.card.values", { count: valores.length }) : null,
              numerar.length ? t("settings.structureFields.card.numberings", { count: numerar.length }) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>
      </div>

      {crear.length > 0 && (
        <ul className="mt-2 space-y-1 text-[12.5px]" data-testid="assistant-estructura-campos">
          {crear.map((o, i) => (
            <li key={i}>
              <span className="font-semibold">{o.etiqueta}</span>
              <span className="text-muted-foreground">
                {" · "}
                {t(`settings.structureFields.levels.${o.nivel}`)} · {t(`settings.structureFields.types.${o.tipo}`)} ·{" "}
                {o.nivel === "cultivo" ? t("settings.structureFields.validities.cultivo") : t(`settings.structureFields.validities.${o.vigencia}`)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {valores.length > 0 && (
        <ul className="mt-2 divide-y divide-[#E4EDF5] rounded-xl border border-[#E4EDF5] text-[12.5px]" data-testid="assistant-estructura-valores">
          {visibles.map((o, i) => (
            <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5">
              <span className="min-w-0 truncate">
                {o.sitio}
                <span className="text-muted-foreground">
                  {" · "}
                  {o.campo}
                  {o.periodo_label ? ` (${o.periodo_label})` : ""}
                </span>
                {o.aproximado && <span className="ml-1 text-amber-700">· {t("settings.structureFields.card.approx")}</span>}
              </span>
              <span className="shrink-0 font-semibold tabular-nums">{o.valor}</span>
            </li>
          ))}
          {valores.length > MUESTRA && !verTodo && (
            <li>
              <button type="button" onClick={() => setVerTodo(true)} className="w-full px-2.5 py-1.5 text-left text-[12px] font-medium text-[#1f5fa8]">
                {t("settings.structureFields.card.showAll", { count: valores.length })}
              </button>
            </li>
          )}
        </ul>
      )}

      {numerar.length > 0 && (
        <ul className="mt-2 space-y-1 text-[12.5px]">
          {numerar.map((o, i) => (
            <li key={i}>
              {t("settings.structureFields.numberTitle", { label: o.campo })}
              {o.periodo_label ? <span className="text-muted-foreground"> · {o.periodo_label}</span> : null}
            </li>
          ))}
        </ul>
      )}

      {tarjeta.noCasan.length > 0 && (
        <div className="mt-2 rounded-xl bg-amber-50 px-2.5 py-2 text-[12px] text-amber-900" data-testid="assistant-estructura-no-casan">
          <p className="font-semibold">{t("settings.structureFields.card.notMatched", { count: tarjeta.noCasan.length })}</p>
          <ul className="mt-1 space-y-0.5">
            {tarjeta.noCasan.slice(0, MUESTRA).map((n, i) => (
              <li key={i} className="truncate">
                {n.dicho} — <span className="opacity-80">{n.motivo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!esAdmin && estado === "idle" && <p className="mt-2 text-xs text-muted-foreground">{t("settings.structureFields.card.onlyAdmin")}</p>}

      {estado === "saved" && resultado?.ok && (
        <p data-testid="assistant-estructura-cambio-saved" className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          {t("settings.structureFields.card.saved", { fields: resultado.campos, values: resultado.valores, numbered: resultado.numerados })}
        </p>
      )}
      {estado === "discarded" && <p className="mt-2 text-xs text-muted-foreground">{t("settings.structureFields.card.discarded")}</p>}
      {error && estado === "error" && <p className="mt-2 text-xs font-medium text-destructive">{error}</p>}

      {esAdmin && (estado === "idle" || estado === "saving" || estado === "error") && (
        <div className="mt-2.5 flex gap-2">
          <button
            type="button"
            onClick={guardar}
            disabled={estado === "saving"}
            data-testid="assistant-estructura-cambio-guardar"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#2E84DE] px-3 text-[13px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {estado === "saving" && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("common.save")}
          </button>
          <button
            type="button"
            onClick={() => setEstado("discarded")}
            disabled={estado === "saving"}
            data-testid="assistant-estructura-cambio-descartar"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl border border-border px-3 text-[13px] font-semibold text-foreground transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {t("settings.structureFields.card.discard")}
          </button>
        </div>
      )}
    </div>
  );
}
